import { FC, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  DataTable,
  InlineNotification,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent,
} from '@carbon/react';
import { CheckmarkOutline, Close, Edit } from '@carbon/react/icons';
import { formatResource } from 'utils/misc';
import { StatusTag, type PartyStatus } from 'components/status-tag';
import type { RcSectionStatus } from './types';
import {
  confirmOverviewAtom,
  createOverviewResourceAtom,
  overviewUrl,
  revertOverviewAtom,
} from './atoms/overview';
import { OverviewEditPanel } from './overview-edit-panel';
import { OVERVIEW_FIELDS, fieldDisplayValue } from './overview-utils';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

interface Props {
  documentId: number | string;
}

const toPartyStatus = (status: RcSectionStatus): PartyStatus => {
  if (status === 'complete') return 'complete';
  if (status === 'not_started') return 'not_started';
  return 'in_progress';
};

const HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

// Mirror of DO's overview-section: a Field/Value DataTable driven by
// OVERVIEW_FIELDS, a StatusTag header, an Edit toolbar button (disabled once
// reviewed), and a primary Mark-as-Correct / Remove-Correct-Status toggle driven
// by the HATEOAS confirmation href. Reuses the shared StatusTag + AFE scss.
export const OverviewSection: FC<Props> = ({ documentId }) => {
  const detailHref = overviewUrl(documentId);
  const detailAtom = useMemo(
    () => createOverviewResourceAtom(detailHref),
    [detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const confirmOverview = useSetAtom(confirmOverviewAtom);
  const revertOverview = useSetAtom(revertOverviewAtom);

  const [editOpen, setEditOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = detail?.status ?? 'not_started';
  const isReviewed = status === 'complete';

  const rows = useMemo(() => {
    if (!detail) return [];
    return OVERVIEW_FIELDS.map((def) => ({
      id: def.key,
      field: def.label,
      value: fieldDisplayValue(def, detail.fields),
    }));
  }, [detail]);

  const confirmHref = detail?.resources?.confirmation?.href;

  const handleMarkCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    confirmOverview({
      url: confirmHref,
      method: 'POST',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to mark as correct.'),
    });
  };

  const handleRemoveCorrect = () => {
    if (!confirmHref) return;
    setActionError(null);
    revertOverview({
      url: confirmHref,
      method: 'DELETE',
      onSuccess: () => reloadDetail(),
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  return (
    <div className={shell.overviewBody}>
      <div className={shell.overviewSectionTitleRow}>
        <h4 className={shell.overviewSectionTitle}>Royalty Check Overview</h4>
        <StatusTag
          status={toPartyStatus(status)}
          progress={isReviewed ? '1/1' : '0/1'}
        />
      </div>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load overview"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading withOverlay={false} small description="Loading overview…" />
      ) : (
        detail && (
          <DataTable rows={rows} headers={HEADERS}>
            {({
              rows: dtRows,
              headers,
              getHeaderProps,
              getTableProps,
            }: any) => (
              <TableContainer>
                <TableToolbar size="sm">
                  <TableToolbarContent>
                    <Button
                      kind="ghost"
                      size="sm"
                      renderIcon={Edit}
                      disabled={isReviewed}
                      onClick={() => setEditOpen(true)}
                    >
                      Edit
                    </Button>
                    {isReviewed ? (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={Close}
                        onClick={handleRemoveCorrect}
                      >
                        Remove Correct Status
                      </Button>
                    ) : (
                      <Button
                        kind="primary"
                        size="sm"
                        renderIcon={CheckmarkOutline}
                        disabled={!confirmHref}
                        onClick={handleMarkCorrect}
                      >
                        Mark as Correct
                      </Button>
                    )}
                  </TableToolbarContent>
                </TableToolbar>
                <Table
                  {...getTableProps()}
                  size="sm"
                  className={styles.identTable}
                >
                  <TableHead>
                    <TableRow>
                      {headers.map((h: any) => (
                        <TableHeader
                          {...getHeaderProps({ header: h })}
                          key={h.key}
                        >
                          {h.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dtRows.map((row: any) => (
                      <TableRow key={row.id}>
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
        )
      )}

      {detail && (
        <OverviewEditPanel
          open={editOpen}
          documentId={documentId}
          detail={detail}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            reloadDetail();
          }}
        />
      )}
    </div>
  );
};
